import { IonInput, isPlatform } from '@ionic/react';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { KeyboardService } from '../../service/keyboard.service';
import {
  forwardTouchEndEventToInput,
  forwardTouchStartEventToInput,
} from '../../util/input';
import { isEnter } from '../../util/keyboard';
import useNumericKeyboard from './numeric-keyboard.hooks';

export interface NumericInputProps {
  ionInputProps?: React.ComponentProps<typeof IonInput> & {
    [key: `data-${string}`]: unknown;
  };
  requireNegative?: boolean;
  children?: React.ReactNode;
}

export interface NumericInputRef extends HTMLIonInputElement {
  inputFocus: (e?: React.TouchEvent) => void;
  inputBlur: (e?: React.TouchEvent) => void;
}

const IOSNumericInput = forwardRef<
  NumericInputRef | undefined,
  NumericInputProps
>((props, ref) => {
  const { ionInputProps, children } = props;

  const internalRef = useRef<HTMLIonInputElement>(null);

  // Get current value from the input (synchronous for toggle functionality)
  const getCurrentValue = useCallback((): string => {
    if (internalRef.current) {
      const inputElement = internalRef.current.querySelector(
        'input'
      ) as HTMLInputElement;
      return inputElement?.value || '0';
    }
    return '0';
  }, []);

  // Handle value changes from the native keyboard
  const handleValueChange = useCallback(async (newValue: string) => {
    const nativeInput = await internalRef.current?.getInputElement();

    if (nativeInput) {
      nativeInput.value = newValue;
      // Dispatch an input event and allow any event listeners to be triggered
      nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, []);

  // Use the numeric keyboard hook
  const { handleNumberInputFocus, handleInputBlur } = useNumericKeyboard({
    getCurrentValue,
    onValueChange: handleValueChange,
  });

  // Expose the internal ref and focus/blur functions through the forwarded ref
  useImperativeHandle(
    ref,
    () => {
      if (!internalRef.current) {
        return undefined;
      }

      const numericInputRef: NumericInputRef = Object.assign(
        internalRef.current,
        {
          inputFocus: handleNumberInputFocus,
          inputBlur: handleInputBlur,
        }
      );

      return numericInputRef;
    },
    [handleNumberInputFocus, handleInputBlur]
  );

  const blurIfEnter = async (e: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (isEnter(e)) {
      const nativeInput = await internalRef.current?.getInputElement();
      nativeInput?.blur();
    }
  };

  return (
    <IonInput
      ref={internalRef}
      type={'number'}
      inputmode={'decimal'}
      debounce={250}
      enterkeyhint={'done'}
      onKeyDown={blurIfEnter}
      onKeyPress={(e) => {
        if (isEnter(e)) {
          e.preventDefault();
        }
      }}
      onTouchStart={(e) => {
        // Forward touch events to the input for better touch handling
        forwardTouchStartEventToInput(e, internalRef.current);
      }}
      onTouchEnd={(e) => {
        forwardTouchEndEventToInput(e, internalRef.current);
      }}
      onIonFocus={() => {
        handleNumberInputFocus();
      }}
      onIonBlur={() => {
        handleInputBlur();
        KeyboardService.hideAccessoryBar();
      }}
      {...ionInputProps}
    >
      {children}
    </IonInput>
  );
});

IOSNumericInput.displayName = 'IOSNumericInput';

const GeneralNumericInput = forwardRef<
  NumericInputRef | undefined,
  NumericInputProps
>((props, ref) => {
  const { ionInputProps, children } = props;

  const internalRef = useRef<HTMLIonInputElement>(null);

  const handleInputFocus = useCallback((e?: React.TouchEvent) => {
    KeyboardService.showAccessoryBar();
    if (e) {
      e.stopPropagation();
      forwardTouchStartEventToInput(e, internalRef.current);
    }
  }, []);

  const handleInputBlur = useCallback((e?: React.TouchEvent) => {
    KeyboardService.hideAccessoryBar();
    if (e) {
      e.stopPropagation();
      forwardTouchEndEventToInput(e, internalRef.current);
    }
  }, []);

  // Expose the internal ref through the forwarded ref
  useImperativeHandle(
    ref,
    () => {
      if (!internalRef.current) {
        return undefined;
      }

      const numericInputRef: NumericInputRef = Object.assign(
        internalRef.current,
        {
          inputFocus: handleInputFocus,
          inputBlur: handleInputBlur,
        }
      );

      return numericInputRef;
    },
    [handleInputFocus, handleInputBlur]
  );

  const blurIfEnter = async (e: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (isEnter(e)) {
      const nativeInput = await internalRef.current?.getInputElement();
      nativeInput?.blur();
    }
  };

  return (
    <IonInput
      ref={internalRef}
      type={'number'}
      inputmode={'decimal'}
      enterkeyhint={'done'}
      onKeyDown={blurIfEnter}
      onKeyPress={(e) => {
        if (isEnter(e)) {
          e.preventDefault();
        }
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        KeyboardService.showAccessoryBar();
        forwardTouchStartEventToInput(e, internalRef.current);
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        KeyboardService.showAccessoryBar();
        forwardTouchEndEventToInput(e, internalRef.current);
      }}
      onIonFocus={() => {
        KeyboardService.showAccessoryBar();
      }}
      onIonBlur={() => {
        KeyboardService.hideAccessoryBar();
      }}
      {...ionInputProps}
    >
      {children}
    </IonInput>
  );
});

GeneralNumericInput.displayName = 'GeneralNumericInput';

const NumericInput = forwardRef<NumericInputRef, NumericInputProps>(
  (props, ref) => {
    const { requireNegative } = props;

    const isIphone = isPlatform('iphone');

    return isIphone && requireNegative ? (
      <IOSNumericInput ref={ref} {...props} />
    ) : (
      <GeneralNumericInput ref={ref} {...props} />
    );
  }
);

NumericInput.displayName = 'NumericInput';

export { IOSNumericInput, GeneralNumericInput, NumericInput };
